/**
 * The onboarding contract (AIC-302/303/305), shared by tracker-api (routes/onboarding.ts validates and stores it) and
 * the analyst portal (onboarding/content.ts writes the copy for these ids). Pure and dependency-free: the portal
 * bundles it.
 *
 * Ids are stable: a saved tour_step or dismissed tip keeps meaning the same thing when the copy changes, so updating
 * content never forces a completed tour on a returning user. Adding a step or tip is additive; renaming one is not.
 */

/** The orientation tour's stops, in order. Each anchors on the Shell rail's nav item of the same page. */
export const TOUR_STEP_IDS = ["today", "assignments", "review"] as const;
export type TourStepId = (typeof TOUR_STEP_IDS)[number];

/** Stable guidance IDs. Result review reuses its copy as persistent decision guidance; saved dismissals remain valid. */
export const TIP_IDS = ["spec_approval", "invite_draft", "result_review"] as const;
export type TipId = (typeof TIP_IDS)[number];

/** Where a tour start came from: the welcome, Getting Started's Replay, or its Resume. */
export const TOUR_SOURCES = ["welcome", "help", "resume"] as const;
export type TourSource = (typeof TOUR_SOURCES)[number];

/**
 * One write to the caller's own onboarding state. Every action only moves progress forward, except the tour cursor
 * (tour_start / tour_step), which is a resume point, not progress.
 *   welcome_shown    the welcome opened (set once; it never auto-opens again)
 *   welcome_skipped  "Explore on my own", Esc or close
 *   tour_start       start, replay (source help) or resume (source resume: from the saved step)
 *   tour_step        the tour moved to a step (Next / Back)
 *   tour_complete    the last step's Done
 *   tour_exit        Exit or Esc mid-tour: the step is kept for Resume
 *   tip_dismiss      a contextual tip dismissed for good
 *   help_open        Getting Started opened (a counter, no event)
 */
export type OnboardingAction =
  | { type: "welcome_shown" }
  | { type: "welcome_skipped" }
  | { type: "tour_start"; source: TourSource }
  | { type: "tour_step"; step: TourStepId }
  | { type: "tour_complete" }
  | { type: "tour_exit" }
  | { type: "tip_dismiss"; tip: TipId }
  | { type: "help_open" };

/**
 * What GET and PUT /api/portal/me/onboarding return. `welcome` is decided on the server: "auto" for any signed-in
 * user (every role) who hasn't seen it; "help_only" once seen and in analyst preview (Help stays available).
 * `version` is 0 while the caller has no row yet.
 */
export interface OnboardingView {
  version: number;
  welcome: "auto" | "help_only";
  welcome_seen: boolean;
  tour: { step: TourStepId | null; completed: boolean; exited: boolean };
  dismissed_tips: TipId[];
  help_opens: number;
}

export const isTourStepId = (v: unknown): v is TourStepId => typeof v === "string" && (TOUR_STEP_IDS as readonly string[]).includes(v);
export const isTipId = (v: unknown): v is TipId => typeof v === "string" && (TIP_IDS as readonly string[]).includes(v);
